import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, NgZone } from '@angular/core';
import { catchError, defer, exhaustMap, from, map, Observable, ReplaySubject, switchMap, take, tap, throwError, timeout, TimeoutError } from 'rxjs';
import { API_BASE_URL } from '../config/api.config';
import { IZIPAY_IPN_URL, IZIPAY_SANDBOX_BUYER, IZIPAY_SDK_URL } from '../config/izipay.config';
import { IzipayBuyer, IzipayConfig, IzipayConstructor, IzipayResponse, IzipayResult, IzipaySession } from '../models/izipay.model';

interface PaymentResponse<T> { result: string; data: T; }

// Cuando se valida, Izipay ya cobró: abortar temprano deja la orden cobrada y sin confirmar, y
// al usuario con un botón para pagar otra vez. Se espera bastante más que en la preparación.
const VALIDATE_TIMEOUT_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class IzipayService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private sdkPromise?: Promise<IzipayConstructor>;

  pay(orderId: string | null, expectedAmount: number, container: string, buyer?: Partial<IzipayBuyer>): Observable<IzipayResult> {
    return defer(() => {
      this.log('Inicio del pago', { orderId, expectedAmount, container });
      if (!orderId || !/^[1-9]\d*$/.test(orderId)) {
        throw new Error('No pudimos preparar tu pedido. Vuelve al resumen de compra.');
      }
      if (!Number.isFinite(expectedAmount) || expectedAmount <= 0) throw new Error('El monto del pedido no es válido.');
      return this.http.get<PaymentResponse<IzipaySession>>(
        `${API_BASE_URL}/izipay/session-token/${encodeURIComponent(orderId)}`,
      ).pipe(
        timeout(15000),
        catchError(error => {
          this.logError('Error al obtener la sesión', error, { orderId });
          return throwError(() => this.sessionError(error));
        }),
        map(response => {
          const session = response.data;
          this.log('Sesión recibida', {
            result: response.result,
            orderId,
            orderNumber: session?.orderNumber,
            transactionId: session?.transactionId,
            merchantCode: session?.merchantCode,
            amount: session?.amount,
            paymentMethods: session?.paymentMethods,
            hasAuthorization: Boolean(session?.authorization),
            hasKeyRSA: Boolean(session?.keyRSA),
          });
          if (response.result !== 'SUCCESS' || !session?.authorization || !session.keyRSA ||
              !session.merchantCode || !session.transactionId || !session.orderNumber ||
              !/^\d+(\.\d{1,2})?$/.test(session.amount) ||
              Math.round(Number(session.amount) * 100) !== Math.round(expectedAmount * 100) ||
              session.orderNumber.replace(/^0+/, '') !== orderId) {
            throw new Error('El pedido cambió o no se pudo validar. Revisa el resumen antes de pagar.');
          }
          return session;
        }),
        switchMap(session => from(this.loadSdk()).pipe(
          switchMap(Sdk => this.open(Sdk, session, container, buyer)),
          exhaustMap(response => this.validateDetached(response, session, orderId)),
          take(1),
        )),
      );
    });
  }

  private sessionError(error: unknown): Error {
    if (error instanceof TimeoutError) return new Error('La preparación del pago tardó demasiado. Intenta nuevamente.');
    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 0: return new Error('No pudimos conectar con el servicio de pagos. Revisa tu conexión e intenta nuevamente.');
        case 401: return new Error('Tu sesión no está disponible o venció. Inicia sesión nuevamente antes de pagar.');
        case 403: return new Error('Tu cuenta no tiene permiso para iniciar este pago.');
        case 404: return new Error('No se encontró el pedido o el servicio para iniciar el pago. Vuelve al resumen de compra.');
        case 400:
        case 422: return new Error('El servicio de pagos rechazó los datos del pedido. Revisa el resumen de compra.');
        default: return new Error('El servicio de pagos no pudo preparar la sesión de Izipay. Intenta más tarde.');
      }
    }
    return new Error('No pudimos preparar la sesión de Izipay. Intenta nuevamente.');
  }

  private loadSdk(): Promise<IzipayConstructor> {
    const browser = this.document.defaultView as (Window & { Izipay?: IzipayConstructor }) | null;
    if (browser?.Izipay) {
      this.log('SDK ya disponible en la página');
      return Promise.resolve(browser.Izipay);
    }
    if (this.sdkPromise) {
      this.log('Esperando una carga previa del SDK');
      return this.sdkPromise;
    }
    this.log('Cargando SDK', { sdkUrl: IZIPAY_SDK_URL });
    this.sdkPromise = new Promise<IzipayConstructor>((resolve, reject) => {
      const script = this.document.createElement('script');
      script.src = IZIPAY_SDK_URL;
      script.defer = true;
      const cleanup = () => { clearTimeout(timer); script.onload = null; script.onerror = null; };
      const fail = () => {
        this.logError('No se pudo cargar el SDK');
        cleanup();
        script.remove();
        reject(new Error('No pudimos cargar Izipay. Revisa tu conexión e intenta nuevamente.'));
      };
      const timer = setTimeout(fail, 15000);
      script.onload = () => {
        if (!browser?.Izipay) { fail(); return; }
        this.log('SDK cargado correctamente');
        cleanup();
        resolve(browser.Izipay);
      };
      script.onerror = fail;
      this.document.head.appendChild(script);
    }).catch(error => { this.sdkPromise = undefined; throw error; });
    return this.sdkPromise;
  }

  private open(Sdk: IzipayConstructor, session: IzipaySession, container: string, buyer?: Partial<IzipayBuyer>): Observable<IzipayResponse> {
    return new Observable(subscriber => {
      const host = this.document.querySelector(container);
      if (!host) {
        this.logError('No se encontró el contenedor del formulario', undefined, { container });
        subscriber.error(new Error('No se pudo mostrar el formulario de pago.'));
        return;
      }
      try {
        const customer = this.normalizeBuyer(buyer);
        const config: IzipayConfig = {
          action: 'pay', merchantCode: session.merchantCode, transactionId: session.transactionId,
          urlIPN: IZIPAY_IPN_URL,
          order: { orderNumber: session.orderNumber, currency: 'PEN', amount: session.amount,
            processType: 'AT', dateTimeTransaction: `${Date.now()}000`,
            merchantBuyerId: `order${session.orderNumber}`, payMethod: session.paymentMethods || 'ALL' },
          billing: { ...customer },
          shipping: { ...customer },
          render: { typeForm: 'embedded', container, showButtonProcessForm: true, autoResize: true },
        };
        this.log('Configuración enviada al SDK', {
          ...config,
          billing: this.maskBuyer(config.billing),
          shipping: this.maskBuyer(config.shipping),
        });
        const checkout = new Sdk({ config });
        const loading = checkout.LoadForm({ authorization: session.authorization, keyRSA: session.keyRSA,
          callbackResponse: response => this.zone.run(() => {
            this.log('Callback recibido', {
              transactionId: session.transactionId,
              code: response?.code,
              hasPayloadHttp: Boolean(response?.payloadHttp),
              hasSignature: Boolean(response?.signature),
            });
            if (!subscriber.closed) subscriber.next(response);
          }),
        });
        if (loading) loading.then(() => this.log('Formulario cargado')).catch(error => this.zone.run(() => {
          this.logError('LoadForm rechazó la carga', error, { transactionId: session.transactionId });
          if (!subscriber.closed) subscriber.error(new Error('No pudimos abrir Izipay. Intenta nuevamente.'));
        }));
      } catch (error) {
        this.logError('Error al construir o abrir el formulario', error, { transactionId: session.transactionId });
        subscriber.error(new Error('No pudimos abrir Izipay. Intenta nuevamente.'));
      }
      return () => host.replaceChildren();
    });
  }

  private normalizeBuyer(buyer?: Partial<IzipayBuyer>): IzipayBuyer {
    const supplied = Object.fromEntries(
      Object.entries(buyer ?? {}).filter((entry): entry is [string, string] =>
        typeof entry[1] === 'string' && entry[1].trim().length > 0),
    ) as Partial<IzipayBuyer>;
    const value: IzipayBuyer = { ...IZIPAY_SANDBOX_BUYER, ...supplied };
    const name = (input: string, fallback: string) => {
      const normalized = input.normalize('NFC').replace(/[^\p{L}\s'-]/gu, '').replace(/\s+/g, ' ').trim();
      return normalized || fallback;
    };
    const documentTypes: Record<string, IzipayBuyer['documentType']> = {
      DNI: 'DNI', CE: 'CE', PASAPORTE: 'PASAPORTE', PASSPORT: 'PASAPORTE', RUC: 'RUC', OTROS: 'OTROS',
    };
    const rawDocumentType = value.documentType.trim().toUpperCase();
    const documentTypeKey = Object.keys(documentTypes).find(key =>
      rawDocumentType === key || rawDocumentType.startsWith(`${key}-`) || rawDocumentType.startsWith(`${key} `),
    );
    return {
      ...value,
      firstName: name(value.firstName, IZIPAY_SANDBOX_BUYER.firstName),
      lastName: name(value.lastName, IZIPAY_SANDBOX_BUYER.lastName),
      documentType: documentTypeKey ? documentTypes[documentTypeKey] : 'OTROS',
    };
  }

  // Cuando llega el callback Izipay ya cobró: la validación con el backend tiene que completarse
  // aunque el consumidor se desuscriba (widget desmontado por expiración del carrito, cierre del
  // modal, navegación); si no, la orden queda cobrada y sin confirmar. La suscripción al HTTP es
  // propia y el resultado se reemite por un ReplaySubject, así desuscribirse no cancela nada.
  private validateDetached(response: IzipayResponse, session: IzipaySession, orderId: string): Observable<IzipayResult> {
    const result = new ReplaySubject<IzipayResult>(1);
    this.validate(response, session, orderId).subscribe(result);
    return result;
  }

  private validate(response: IzipayResponse, session: IzipaySession, orderId: string): Observable<IzipayResult> {
    return defer(() => {
      if (!response?.payloadHttp || !response.signature) {
        this.logError('Callback incompleto', undefined, {
          transactionId: session.transactionId,
          hasPayloadHttp: Boolean(response?.payloadHttp),
          hasSignature: Boolean(response?.signature),
        });
        throw new Error('El pago no se completó. Puedes volver a intentarlo.');
      }
      // Check only the original signed payload; do not trust the outer callback fields.
      const payload = JSON.parse(response.payloadHttp);
      const order = Array.isArray(payload.response?.order) ? payload.response.order[0] : payload.response?.order;
      this.log('Respuesta firmada recibida', {
        code: payload.code,
        transactionId: payload.transactionId,
        orderNumber: order?.orderNumber,
        currency: order?.currency,
        amount: order?.amount,
      });
      if (payload.code !== '00') throw new Error('El pago no fue aprobado. Puedes intentar con otro medio de pago.');
      if (payload.transactionId !== session.transactionId || order?.orderNumber !== session.orderNumber ||
          order?.currency !== 'PEN' || !Number.isFinite(Number(order?.amount)) ||
          Math.round(Number(order.amount) * 100) !== Math.round(Number(session.amount) * 100)) {
        throw new Error('No pudimos confirmar el pago de este pedido.');
      }
      return this.http.post<PaymentResponse<{ success: boolean }>>(`${API_BASE_URL}/izipay/validate`, {
        payloadHttp: response.payloadHttp, signature: response.signature,
      }).pipe(timeout(VALIDATE_TIMEOUT_MS), tap(result => this.log('Validación del backend recibida', {
        transactionId: session.transactionId,
        result: result.result,
        success: result.data?.success,
      })), catchError(error => {
        this.logError('Error al validar el pago con el backend', error, { transactionId: session.transactionId });
        return throwError(() =>
          new Error('No pudimos verificar el resultado del pago. Revisa el estado de tu pedido antes de volver a pagar.'));
      }), map(result => {
        if (result.result !== 'SUCCESS' || result.data?.success !== true) {
          throw new Error('No pudimos confirmar el pago. Revisa el estado de tu pedido antes de reintentar.');
        }
        return { orderId, numeroTransaccion: session.transactionId, amount: Number(session.amount) };
      }));
    });
  }

  private log(message: string, details?: unknown): void {
    console.info(`[Izipay] ${message}`, details ?? '');
  }

  private maskBuyer(buyer: IzipayBuyer): Record<string, string> {
    return {
      ...buyer,
      firstName: '[OCULTO]',
      lastName: '[OCULTO]',
      email: '[OCULTO]',
      phoneNumber: '[OCULTO]',
      street: '[OCULTO]',
      document: '[OCULTO]',
    };
  }

  private logError(message: string, error?: unknown, details?: unknown): void {
    const errorDetails = error instanceof HttpErrorResponse
      ? { type: 'HttpErrorResponse', status: error.status, statusText: error.statusText, url: error.url }
      : error instanceof Error
        ? { type: error.name, message: error.message }
        : error == null ? undefined : { type: typeof error };
    console.error(`[Izipay] ${message}`, { ...((details as object | undefined) ?? {}), error: errorDetails });
  }
}
